import { useQueryClient } from '@tanstack/react-query'
import type { MouseEvent } from 'react'
import { useLocation, type Location } from 'react-router'
import { photoQuery } from './albums'
import { reducedMotion } from './motion'

// A photo opens over the page it was tapped on, growing out of the spot where it sat, and shrinks back into that spot
// when it's closed (PhotoPage). The page stays underneath the whole time, as it was, so closing lands you exactly
// where you were. Every place that shows photos to open uses photoLink, which carries the page along as the
// address's `background` and notes where the tapped photo sat.

export type Over = { background?: Location }

// The page being looked at: the one under an open photo, or else the address itself.
export function useShownLocation() {
  const location = useLocation()
  return (location.state as Over | null)?.background ?? location
}

// Where the tapped photo sat on the screen, and the copy of it already loaded, for the photo to grow out of.
type Origin = { id: string; rect: DOMRect; src: string; radius: number; at: number }
let origin: Origin | null = null

const imageIn = (el: Element | null) => (el instanceof HTMLImageElement ? el : (el?.querySelector('img') ?? null))

function note(id: string, from: Element) {
  const img = imageIn(from)
  if (!img) return
  origin = {
    id,
    rect: img.getBoundingClientRect(),
    src: img.currentSrc || img.src,
    radius: tileRadius(img),
    at: performance.now(),
  }
}

// The rounded corner the photo shows with in its tile (its own, or its frame's).
export function tileRadius(img: HTMLElement) {
  for (let el: HTMLElement | null = img; el && el !== document.body; el = el.parentElement) {
    const radius = parseFloat(getComputedStyle(el).borderTopLeftRadius)
    if (radius) return radius
    if (el.dataset.photoId) break
  }
  return 0
}

// The origin of the photo just opened, once: a photo opened any other way (a link, the address) has none.
export function takeOrigin(id: string) {
  const found = origin
  origin = null
  return found && found.id === id && performance.now() - found.at < 2000 ? found : null
}

// The way into a photo from a page: its address, the page to keep under it, and the spot it grows from.
// `data-photo-id` marks the tile so the photo can find it again to shrink back into.
// What's known about the photo is fetched as a finger or the pointer comes down on it, a head start on the tap.
export function usePhotoLink() {
  const location = useLocation()
  const queryClient = useQueryClient()
  // Opened from over another photo (a related one), it still sits over the same page.
  const background = (location.state as Over | null)?.background ?? location
  return (id: string) => ({
    to: `/photo/${id}`,
    state: { background } satisfies Over,
    'data-photo-id': id,
    onPointerDown: () => void queryClient.prefetchQuery(photoQuery(id)),
    onClick: (e: MouseEvent<HTMLElement>) => note(id, e.currentTarget),
  })
}

// The tile of a photo on the page under the viewer, if it's there.
export function tileOf(id: string) {
  const tile = document.querySelector(`[data-photo-id="${CSS.escape(id)}"]:not(.viewer-room *)`)
  return imageIn(tile)
}

// What it takes for the photo, whole at `to`, to show exactly as it did cropped into `from` (a tile shows the
// middle of the photo, filling its square): a move and a scale, a clip for the parts the tile cut off, and its
// corners. Applied to the photo where it lands and played back to nothing, the photo grows out of the tile.
type Box = Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>
export function fromTile(from: { rect: Box; radius: number }, to: Box) {
  const scale = Math.max(from.rect.width / to.width, from.rect.height / to.height)
  const x = from.rect.left + from.rect.width / 2 - (to.left + to.width / 2)
  const y = from.rect.top + from.rect.height / 2 - (to.top + to.height / 2)
  const clipX = Math.max(0, (to.width - from.rect.width / scale) / 2)
  const clipY = Math.max(0, (to.height - from.rect.height / scale) / 2)
  const radius = from.radius / scale
  return {
    transform: `translate(${x}px, ${y}px) scale(${scale})`,
    clipPath: `inset(${clipY}px ${clipX}px round ${radius}px)`,
  }
}

export const smooth = () => !reducedMotion()

// The page under an open photo: hidden from screen readers at once, and from sight once the photo has finished
// opening (it's covered then anyway), so nothing there is found twice. It shows again for the photo to shrink back.
export function coverPage(state: 'opening' | 'open' | 'closing' | null) {
  const root = document.documentElement
  if (state) root.dataset.photoOver = state
  else delete root.dataset.photoOver
}
